import { sanitizeContractHtml } from './sanitizer';

/**
 * Escapa entidades HTML en cadenas de texto plano para evitar interpretación de etiquetas.
 */
const escaparHtml = (texto) =>
  (texto || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * Normaliza el código HTML pegado desde fuentes como Microsoft Word, Google Docs u otros navegadores:
 * - Extrae el fragmento principal si existen delimitadores de portapapeles.
 * - Limpia comentarios condicionales, estilos en bloque, meta tags y etiquetas específicas de office.
 * - Normaliza negritas y estilos tipográficos.
 * - Convierte automáticamente tokens de variables {{clave}} o <b>{{clave}}</b> en chips interactivos.
 *
 * @param {string} rawHtml - HTML original del portapapeles
 * @returns {string} HTML normalizado
 */
export const normalizarHtmlPegado = (rawHtml) => {
  if (!rawHtml || typeof rawHtml !== 'string') return '';

  let cleanHtml = rawHtml;

  // 1. Extraer fragmento principal si existe <!--StartFragment-->...<!--EndFragment--> ANTES de quitar comentarios
  const fragmentMatch = cleanHtml.match(/<!--StartFragment-->([\s\S]*?)<!--EndFragment-->/i);
  if (fragmentMatch) {
    cleanHtml = fragmentMatch[1];
  } else {
    const bodyMatch = cleanHtml.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
    if (bodyMatch) {
      cleanHtml = bodyMatch[1];
    }
  }

  cleanHtml = cleanHtml
    // Quitar comentarios y directivas condicionales de Word
    .replace(/<!--[\s\S]*?-->/gi, '')
    // Quitar estilos en bloque y meta etiquetas
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .replace(/<link[^>]*>/gi, '')
    .replace(/<\/?(?:o|w|m|v):[^>]*>/gi, '')
    // Desempaquetar span/b contenedores de Google Docs (docs-internal-guid-...)
    .replace(/<(?:span|b)[^>]*id=["']docs-internal-guid-[^"']*["'][^>]*>([\s\S]*?)<\/(?:span|b)>/gi, '$1')
    // Normalizar font-weight numérico (700, 800, 900) y mso-bidi a font-weight: bold
    .replace(/font-weight\s*:\s*(?:[6-9]00|bolder)/gi, 'font-weight: bold')
    .replace(/mso-bidi-font-weight\s*:\s*bold/gi, 'font-weight: bold')
    // Limpiar estilos redundantes de Google Docs que inflan el HTML
    .replace(/vertical-align\s*:\s*baseline;?/gi, '')
    .replace(/background-color\s*:\s*transparent;?/gi, '')
    // Quitar nowrap que impida el salto de línea visual
    .replace(/white-space\s*:\s*nowrap;?/gi, '');

  // Convertir automáticamente cualquier variable {{clave}} o <b>{{clave}}</b> pegada en chip interactivo
  cleanHtml = cleanHtml.replace(
    /(<(?:b|strong)[^>]*>\s*)?\{\{([a-zA-Z0-9_.]+)\}\}(\s*<\/(?:b|strong)>)?/gi,
    (match, openB, key, closeB) => {
      const isBold = Boolean(openB && closeB);
      if (isBold) {
        return `<b class="variable-wrapper-bold" data-negrita="true"><span class="variable-chip chip-bold" contenteditable="false" data-clave="${key}" data-negrita="true" title="Variable en negrita. Clic para alternar"><b>{{${key}}}</b></span></b>`;
      }
      return `<span class="variable-chip" contenteditable="false" data-clave="${key}" title="Clic para poner en negrita">{{${key}}}</span>`;
    }
  );

  return cleanHtml;
};

/**
 * Inserta un fragmento de HTML sanitizado en la posición actual del cursor dentro del editor
 * utilizando la Range API para evitar truncamientos por límite de caracteres de execCommand.
 *
 * @param {string} sanitizedHtml - HTML seguro a insertar
 * @param {Object} editorRef - Referencia al elemento DOM contentEditable
 */
export const insertarHtmlEnSeleccion = (sanitizedHtml, editorRef) => {
  const selection = window.getSelection();
  let insertadoConExito = false;

  if (
    selection &&
    selection.rangeCount > 0 &&
    editorRef?.current &&
    editorRef.current.contains(selection.anchorNode)
  ) {
    try {
      const range = selection.getRangeAt(0);
      range.deleteContents();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedHtml;
      const frag = document.createDocumentFragment();
      let lastNode = null;
      while (tempDiv.firstChild) {
        lastNode = tempDiv.firstChild;
        frag.appendChild(lastNode);
      }
      range.insertNode(frag);
      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      insertadoConExito = true;
    } catch (err) {
      insertadoConExito = false;
    }
  }

  if (!insertadoConExito) {
    document.execCommand('insertHTML', false, sanitizedHtml);
  }
};

/**
 * Procesa el evento de pegado en el editor:
 * Todo payload (HTML o texto plano) atraviesa obligatoriamente la frontera de sanitización con DOMPurify.
 *
 * @param {ClipboardEvent} e - Evento nativo onPaste
 * @param {Object} editorRef - Referencia al contenedor contentEditable
 * @param {Function} notificarCambio - Función para notificar cambios y actualizar estadísticas
 */
export const procesarPegado = (e, editorRef, notificarCambio) => {
  const clipboardData = e.clipboardData || window.clipboardData;
  if (!clipboardData) return;

  const html = clipboardData.getData('text/html');
  const plainText = clipboardData.getData('text/plain');

  // Si el portapapeles incluye HTML, se intercepta y se normaliza/sanitiza SIEMPRE
  if (html) {
    e.preventDefault();
    const normalizado = normalizarHtmlPegado(html);
    const sanitizedHtml = sanitizeContractHtml(normalizado);
    insertarHtmlEnSeleccion(sanitizedHtml, editorRef);
    if (notificarCambio) notificarCambio();
    return;
  }

  // Si se pega texto plano, se convierten posibles variables a chips, se preservan saltos de línea y se sanitiza
  if (plainText) {
    e.preventDefault();
    const textoEscapado = escaparHtml(plainText);
    const htmlConChips = textoEscapado
      .replace(/\n/g, '<br>')
      .replace(
        /\{\{([a-zA-Z0-9_.]+)\}\}/g,
        '<span class="variable-chip" contenteditable="false" data-clave="$1" title="Clic para poner en negrita">{{$1}}</span>'
      );
    const sanitizedHtml = sanitizeContractHtml(htmlConChips);
    insertarHtmlEnSeleccion(sanitizedHtml, editorRef);
    if (notificarCambio) notificarCambio();
  }
};

export default procesarPegado;
